import {
  EntityType,
  RiskLevel,
  type RiskEntityCode,
  type RiskEntityTypeStats,
} from "@/core/administration/entities/RiskEntityCode";
import type { RiskEntityHistoryEvent, HistoryAction } from "@/core/administration/entities/RiskEntityHistoryEvent";
import type {
  ImportDiff,
  ImportDiffRow,
  ImportResult,
  PreviewImportResult,
} from "@/core/administration/entities/RiskEntityImport";

// ---------------------------------------------------------------------------
// API DTO shapes (private to the details/ layer)
// ---------------------------------------------------------------------------

export interface EntityReferentialDto {
  id: number;
  entity: string;
  code: string;
  label: string;
  riskLevel: string;
  version: number;
  active: boolean;
  current: boolean;
  createdAt: string;
  createdBy: string;
}

export interface ScoringEntityAuditDto {
  id: number;
  entity: string;
  code: string;
  action: string;
  oldLevel: string | null;
  newLevel: string | null;
  oldLabel: string | null;
  newLabel: string | null;
  reason: string;
  modifiedBy: string;
  modifiedAt: string;
}

export interface CsvRowDiffDto {
  code: string;
  label: string;
  oldLevel: string | null;
  newLevel: string | null;
}

export interface CsvImportErrorDto {
  line: number;
  code: string;
  reason: string;
}

export interface CsvImportPreviewDto {
  entity: string;
  importToken: string;
  modified: CsvRowDiffDto[];
  added: CsvRowDiffDto[];
  unchangedCount: number;
  errors: CsvImportErrorDto[];
}

export interface CsvImportSummaryDto {
  entity: string;
  applied: number;
  added: number;
  skippedErrors: number;
}

// ---------------------------------------------------------------------------
// Mappers
// ---------------------------------------------------------------------------

// Use explicit branching to avoid dynamic object access patterns
// flagged by `detect-object-injection`.
function mapAuditAction(action: string): HistoryAction {
  switch (action) {
    case "CREATE": return "ADD";
    case "UPDATE": return "MODIFY";
    case "DEACTIVATE": return "DEACTIVATE";
    case "ROLLBACK": return "ROLLBACK";
    case "IMPORT_CSV": return "ADD";
    default: return "ADD";
  }
}

export function mapToRiskEntityCode(dto: EntityReferentialDto): RiskEntityCode {
  return {
    id: String(dto.id),
    type: dto.entity as EntityType,
    code: dto.code,
    libelle: dto.label,
    level: dto.riskLevel as RiskLevel,
    active: dto.active,
    lastModifiedAt: dto.createdAt,
    lastModifiedBy: dto.createdBy,
  };
}

export function mapToHistoryEvent(dto: ScoringEntityAuditDto): RiskEntityHistoryEvent {
  return {
    id: String(dto.id),
    type: dto.entity as EntityType,
    code: dto.code,
    libelle: dto.newLabel ?? dto.oldLabel ?? "",
    action: mapAuditAction(dto.action),
    oldLevel: dto.oldLevel ? (dto.oldLevel as RiskLevel) : undefined,
    newLevel: dto.newLevel ? (dto.newLevel as RiskLevel) : undefined,
    reason: dto.reason ?? "",
    actor: dto.modifiedBy,
    timestamp: dto.modifiedAt,
  };
}

export function mapToPreviewResult(dto: CsvImportPreviewDto): PreviewImportResult {
  const rows: ImportDiffRow[] = [
    ...dto.added.map((r): ImportDiffRow => ({
      code: r.code,
      label: r.label,
      oldLevel: null,
      newLevel: r.newLevel as RiskLevel | null,
      status: "added",
    })),
    ...dto.modified.map((r): ImportDiffRow => ({
      code: r.code,
      label: r.label,
      oldLevel: r.oldLevel as RiskLevel | null,
      newLevel: r.newLevel as RiskLevel | null,
      status: "modified",
    })),
    ...dto.errors.map((e): ImportDiffRow => ({
      code: e.code,
      label: "",
      oldLevel: null,
      newLevel: null,
      status: "invalid",
      errorReason: e.reason,
    })),
  ];

  const diff: ImportDiff = {
    rows,
    addedCount: dto.added.length,
    modifiedCount: dto.modified.length,
    unchangedCount: dto.unchangedCount,
    invalidCount: dto.errors.length,
  };

  return { diff, importToken: dto.importToken };
}

export function mapToImportResult(dto: CsvImportSummaryDto): ImportResult {
  return {
    applied: dto.applied,
    added: dto.added,
    errors: dto.skippedErrors,
  };
}

export function mapToTypeStats(
  type: EntityType,
  items: EntityReferentialDto[],
): RiskEntityTypeStats {
  const activeItems = items.filter((i) => i.active);
  const lastModifiedAt = activeItems.reduce((max, item) => {
    const d = new Date(item.createdAt);
    return d > max ? d : max;
  }, new Date(0));

  return {
    type,
    count: activeItems.length,
    lastModifiedAt: activeItems.length > 0 ? lastModifiedAt.toISOString() : "",
  };
}
